import { useEffect, useId, useMemo, useRef, useState } from "react";
import { geoArea, geoCentroid, geoEqualEarth, geoGraticule, geoOrthographic, geoPath } from "d3-geo";
import { feature, mesh } from "topojson-client";
import world from "world-atlas/countries-110m.json";
import SMALL from "./small-countries.json";
import { nf } from "./shared";
import { useMeasure, useZoomPan, ZoomControls } from "./zoom";

// Charts for the Knowledge Base, following the dataviz method:
//   - single-series charts use categorical slot 1 (blue); no legend box, the title names it
//   - magnitude on the map is one blue ramp, light→dark on light themes and dark→light on dark
//   - origin vs target uses the two validated slots (blue, orange) plus a legend and labels
//   - every chart has a hover tooltip and a table twin so values are never color-only
// Palette steps come from the validated reference palette; both theme variants were run
// through validate_palette.js against this app's card surfaces.

const isDarkTheme = (B) => {
  const hex = (B.bgCard || "#252528").replace("#", "");
  const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.5;
};

export function vizTokens(B) {
  const dark = isDarkTheme(B);
  // Maps: sea a cool step away from the card (darker on dark themes, lighter on light), so
  // land reads as land; graticule a hairline on the sea; halo the ring round marks on a map,
  // past both land and sea so it shows on either.
  return dark ? {
    dark, series1: "#3987e5", series2: "#d95926",
    ramp: ["#104281", "#1c5cab", "#2a78d6", "#5598e7", "#86b6ef"],
    empty: "#323235", grid: "#2c2c2a", axis: "#383835", ink: B.text, inkMuted: B.textMuted, inkDim: B.textDim, surface: B.bgCard,
    sea: "#1b1e24", graticule: "#2a2f38", halo: "#141518", place: "#6b6b73",
  } : {
    dark, series1: "#256abf", series2: "#c94f1f",
    ramp: ["#b7d3f6", "#86b6ef", "#5598e7", "#2a78d6", "#184f95"],
    empty: "#d4d6da", grid: "#d1d3d8", axis: "#b8bbc2", ink: B.text, inkMuted: B.textMuted, inkDim: B.textDim, surface: B.bgCard,
    sea: "#eef2f7", graticule: "#d5dde7", halo: "#ffffff", place: "#a5a9b1",
  };
}

export function useWidth(ref, fallback = 720) {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(260, Math.round(e.contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

// Tooltip: value leads, label follows. Positioned inside the chart container.
export function Tooltip({ tip, ui }) {
  const { B, fs } = ui;
  if (!tip) return null;
  return (
    <div style={{
      position: "absolute", left: tip.x, top: tip.y, transform: `translate(${tip.flip ? "-100%" : "0"}, -100%) translate(${tip.flip ? -10 : 10}px, -8px)`,
      pointerEvents: "none", zIndex: 5, background: B.bgPanel, border: `1px solid ${B.borderLight}`, borderRadius: 8,
      padding: "7px 10px", boxShadow: "0 8px 24px rgba(0,0,0,0.35)", width: "max-content", maxWidth: 280, // max-content: near the right edge, it would shrink to the space left
    }}>
      <div style={{ fontSize: fs(14), fontWeight: 800, color: B.text }}>{tip.value}</div>
      <div style={{ fontSize: fs(11), color: B.textMuted }}>{tip.label}</div>
      {tip.detail && <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 4, lineHeight: 1.45, whiteSpace: "pre-line" }}>{tip.detail}</div>}
    </div>
  );
}

export function TableToggle({ on, onChange, ui }) {
  const { B, fs } = ui;
  return (
    <button data-noprint onClick={() => onChange(!on)} style={{ background: "none", border: `1px solid ${B.border}`, borderRadius: 6, color: B.textMuted, cursor: "pointer", fontSize: fs(11), padding: "3px 9px", fontFamily: "'Satoshi', sans-serif" }}>
      {on ? "Show chart" : "Show table"}
    </button>
  );
}

// ─── Motion on the maps ───
// Dots that travel along a line, from where something set out to where it went, and rings that
// pulse at the busiest origins. SMIL, so the browser runs them without React drawing again; none
// when the reader asks for less motion. Marked data-motion, so a saved picture leaves them out.
export function useReducedMotion() {
  const [still, setStill] = useState(() => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const q = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!q) return undefined;
    const on = () => setStill(q.matches);
    q.addEventListener?.("change", on);
    return () => q.removeEventListener?.("change", on);
  }, []);
  return still;
}

// A steady offset from the line itself, so dots on different lines don't set out together.
const phaseOf = (d) => { let h = 0; for (let i = 0; i < d.length; i += 7) h = (h * 31 + d.charCodeAt(i)) >>> 0; return (h % 1000) / 1000; };

/**
 * Dots along the path d, drawn inside a zoom transform of scale k (sizes are divided by it, so they
 * stay the same on screen). len is about how long the line is in the drawing's units, for an even
 * pace; n dots, spread along it. A dot turns from `from` to `to` on the way, like the arcs.
 */
export function FlowDots({ d, len, n = 1, k = 1, r = 2, from, to = from, pace = 70 }) {
  if (!d) return null;
  const dur = Math.min(9, Math.max(1.8, len / pace));
  const phase = phaseOf(d);
  return (
    <g data-motion="" pointerEvents="none">
      {Array.from({ length: n }, (_, i) => {
        const timing = { dur: `${dur.toFixed(2)}s`, begin: `${(-dur * ((phase + i / n) % 1)).toFixed(2)}s`, repeatCount: "indefinite" };
        const tint = from !== to ? <animate attributeName="fill" values={`${from};${to}`} {...timing} /> : null;
        return (
          <g key={i} opacity={0}>
            <circle r={(r * 2.8) / k} fill={from} fillOpacity={0.28}>{tint}</circle>
            <circle r={r / k} fill={from}>{tint}</circle>
            <circle r={(r * 0.45) / k} fill="#fff" fillOpacity={0.85} />
            <animateMotion path={d} {...timing} />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.12;0.85;1" {...timing} />
          </g>
        );
      })}
    </g>
  );
}

/** Rings pulsing out from (x, y), in screen units, round a mark of radius r, with a soft glow. */
export function Pulse({ x, y, r = 5, color, reach = 14, dur = 2.6 }) {
  return (
    <g data-motion="" pointerEvents="none">
      <circle cx={x} cy={y} r={r + 3} fill={color} fillOpacity={0.18} />
      {[0, 1].map(i => {
        const timing = { dur: `${dur}s`, begin: `${(-i * dur) / 2}s`, repeatCount: "indefinite" };
        return (
          <circle key={i} cx={x} cy={y} r={r} fill="none" stroke={color} strokeWidth={1.5} strokeOpacity={0}>
            <animate attributeName="r" values={`${r};${r + reach}`} {...timing} />
            <animate attributeName="stroke-opacity" values="0.9;0" {...timing} />
          </circle>
        );
      })}
    </g>
  );
}

export function SimpleTable({ head, rows, ui }) {
  const { B, fs } = ui;
  return (
    <div style={{ maxHeight: 360, overflowY: "auto", border: `1px solid ${B.border}`, borderRadius: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs(12) }}>
        <thead>
          <tr>{head.map((h, i) => <th key={i} style={{ textAlign: i ? "right" : "left", padding: "8px 12px", color: B.textDim, fontWeight: 700, borderBottom: `1px solid ${B.border}`, position: "sticky", top: 0, background: B.bgCard }}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <td key={j} style={{ textAlign: j ? "right" : "left", padding: "7px 12px", color: j ? B.text : B.textMuted, borderTop: i ? `1px solid ${B.border}` : "none", fontVariantNumeric: j ? "tabular-nums" : "normal" }}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Choropleth ───
// Natural Earth at 1:110m, which leaves out 61 small countries and territories (Singapore,
// Hong Kong, Bahrain, Malta…): small-countries.json has a point for each (from
// scripts/build-small-countries.js). A country with data that's too small to point at is
// drawn as a dot, which keeps its size as the map zooms; zoomed in, the map swaps to 1:50m.
// world-atlas gives Kosovo no id; the server's code for it is 983. Antarctica is dropped.
const withIds = (features) => features.filter(f => f.id !== "010").map(f => (f.id || f.properties.name !== "Kosovo" ? f : { ...f, id: "983" }));
const COUNTRIES = withIds(feature(world, world.objects.countries).features);
const SMALL_AT = new Map(SMALL.map(c => [c.id, c.at]));
const DOT_BELOW = 6; // px across, at the current zoom, on a map 900 px wide; less on a narrower one (a phone's would be all dots)
const FINE_FROM = 2; // zoom at which the map swaps to 1:50m

// The world's land at 1:110m, as one shape: enough for a small locator globe.
export const LAND = feature(world, world.objects.land);

// Natural Earth at 1:50m, loaded once and only when a map needs it: the missile map, and a
// world map zoomed in. Borders are the lines between countries, coast the rest.
let landPromise = null;
export const loadLand = () => (landPromise ||= import("world-atlas/countries-50m.json").then(m => {
  const topo = m.default || m;
  const o = topo.objects.countries;
  return { countries: feature(topo, o).features, borders: mesh(topo, o, (a, b) => a !== b), coast: mesh(topo, o, (a, b) => a === b) };
}));
let fineCountries = null;

// Dots closer than `gap` px (Hong Kong and Macao, Jersey and Guernsey, the Lesser Antilles on a
// world map) are pushed apart until each can be seen and pointed at. Zoomed in, they're far
// enough apart to stay where they belong.
function spreadDots(xys, gap = 10) {
  const p = xys.map(([x, y]) => [x, y]);
  for (let round = 0; round < 10; round++) {
    let moved = false;
    for (let i = 0; i < p.length; i++) {
      for (let j = i + 1; j < p.length; j++) {
        const dx = p[j][0] - p[i][0], dy = p[j][1] - p[i][1], dist = Math.hypot(dx, dy);
        if (dist >= gap) continue;
        const push = (gap - dist) / 2, ux = dist ? dx / dist : 1, uy = dist ? dy / dist : 0;
        p[i][0] -= ux * push; p[i][1] -= uy * push;
        p[j][0] += ux * push; p[j][1] += uy * push;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return p;
}

// Nice class breaks on a log scale: 1 … max in five steps.
export function breaksFor(max) {
  if (max <= 5) return [1, 2, 3, 4, 5].filter(x => x <= Math.max(1, max));
  const nice = (x) => {
    const p = Math.pow(10, Math.floor(Math.log10(x)));
    const m = x / p;
    return (m < 1.5 ? 1 : m < 3.5 ? 2 : m < 7.5 ? 5 : 10) * p;
  };
  const out = [1];
  for (let i = 1; i < 5; i++) {
    const v = nice(Math.pow(max, i / 5));
    if (v > out[out.length - 1]) out.push(v);
  }
  return out;
}

// Linear class breaks: five equal steps from 0 to a round maximum, for shares (where 10% to 20%
// is as far as 80% to 90%).
export const linearBreaks = (max) => { const step = niceMax(Math.max(1, max)) / 5; return [0, 1, 2, 3, 4].map(i => i * step); };
// "1 listing", "2 listings"; a unit that isn't a plural noun ("% of tests") stays as it is.
const unitFor = (unit, n) => (n === 1 && /[a-z]s$/.test(unit) && !/%/.test(unit) ? unit.slice(0, -1) : unit);
// The region buttons: longitude and latitude boxes to zoom to.
const REGIONS = [
  ["World", null], ["Americas", [[-170, -56], [-34, 72]]], ["Europe", [[-25, 34], [45, 71]]], ["Middle East", [[25, 12], [63, 42]]],
  ["Africa", [[-18, -35], [52, 37]]], ["Asia", [[60, -11], [150, 55]]], ["Oceania", [[110, -48], [180, 0]]],
];

/**
 * mode "count":    values = { ISO2: n }, numeric = { ISO2: "840" }, names = { ISO2: "United States" }
 * mode "mentions": mentions = [{ iso2, numeric, name, kind: "origin"|"target"|"place", sentence, plain }]
 *                  (plain: the sentence goes in the tooltip as it is, without "Origin:" before it)
 * measured:        the ISO2 codes that have data, zeros included (values leaves zeros out). With
 *                  it, a country at zero is plain ("0") and one without data is hatched ("No data").
 * scale:           "log" for counts, "linear" for shares.
 * tools:           the region buttons and the find box (off for small maps, like dashboard tiles).
 * labels:          mentions mode: words for origin, target, place (a country named, neither) and none.
 * arcs:            [{ from, to, n, label, detail }]: numeric ids, an arc from one to the other,
 *                  orange at the origin end and blue at the target end, thicker for a bigger n;
 *                  onArc(arc) on a click.
 * points:          [{ key, at: [lon, lat], label, detail }]: exact places, as small diamonds;
 *                  onPoint(point) on a click.
 *
 * Equal Earth, so countries keep their true sizes against each other. The legend is a filter:
 * pointing at a class lights its countries, clicking keeps them lit.
 */
const KIND_LABELS = { origin: "Attributed origin", target: "Named as target", place: "Named", none: "Not mentioned" };
const KIND_PREFIX = { origin: "Origin", target: "Target", place: "Named" };
const KIND_RANK = { origin: 0, target: 1, place: 2 };
export function ChoroplethMap({ mode = "count", values = {}, numeric = {}, names = {}, mentions = [], unit = "victims", measured = null, scale = "log", tools = true, labels = null, arcs = [], points = [], onPoint, onArc, ui, onSelect }) {
  const L = { ...KIND_LABELS, ...labels };
  const { B, fs } = ui;
  const t = vizTokens(B);
  const hatch = `hatch${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const box = useMeasure(900);
  const width = box.width;
  const height = Math.round(width * 0.5);
  const [tip, setTip] = useState(null);
  const [table, setTable] = useState(false);
  const [hoverId, setHoverId] = useState(null); // the country under the pointer
  const [focusId, setFocusId] = useState(null); // the country picked in the find box
  const [find, setFind] = useState("");
  const [legendHot, setLegendHot] = useState(null); // a legend class under the pointer
  const [legendPin, setLegendPin] = useState(null); // a legend class clicked
  const z = useZoomPan(box.el, { width, height, maxK: 10, resetKey: table });
  const [fine, setFine] = useState(fineCountries);
  const wantFine = z.t.k >= FINE_FROM;
  useEffect(() => {
    if (!wantFine || fine) return undefined;
    let live = true;
    loadLand().then(l => { fineCountries ||= withIds(l.countries); if (live) setFine(fineCountries); }).catch(() => {});
    return () => { live = false; };
  }, [wantFine, fine]);
  const features = wantFine && fine ? fine : COUNTRIES;

  // Fitted to the 1:110m map either way, so the swap to 1:50m doesn't move anything.
  const projection = useMemo(() => geoEqualEarth().fitSize([width, height], { type: "FeatureCollection", features: COUNTRIES }), [width, height]);
  const path = useMemo(() => geoPath(projection), [projection]);
  // The sea (the globe's outline) and a graticule every 30°, under the countries.
  const globe = useMemo(() => ({ sea: path({ type: "Sphere" }), grid: path(geoGraticule().step([30, 30])()) }), [path]);
  // Each country's outline, how big it is on the map, and where its dot would go.
  const shapes = useMemo(() => features.map(f => {
    const outline = path(f);
    if (!outline) return null;
    const [[x0, y0], [x1, y1]] = path.bounds(f);
    return { f, key: f.id || f.properties.name, outline, size: Math.max(x1 - x0, y1 - y0), at: SMALL_AT.has(f.id) ? projection(SMALL_AT.get(f.id)) : path.centroid(f) };
  }).filter(Boolean), [features, path, projection]);

  // numeric id → datum
  const byNumeric = useMemo(() => {
    const m = new Map();
    if (mode === "count") {
      for (const [iso2, n] of Object.entries(values)) if (numeric[iso2]) m.set(numeric[iso2], { iso2, n, name: names[iso2] || iso2 });
    } else {
      for (const x of mentions) {
        if (!x.numeric) continue;
        const prev = m.get(x.numeric);
        // Origin wins the fill if a country is somehow both (then target, then named); every sentence stays in the tooltip.
        m.set(x.numeric, { ...x, sentences: [...(prev?.sentences || []), x.plain ? x.sentence : `${KIND_PREFIX[x.kind]}: ${x.sentence}`], kind: prev && KIND_RANK[prev.kind] < KIND_RANK[x.kind] ? prev.kind : x.kind });
      }
    }
    return m;
  }, [mode, values, numeric, names, mentions]);

  const measuredIds = useMemo(() => (measured ? new Set(measured.map(c => numeric[c]).filter(Boolean)) : null), [measured, numeric]);

  const max = mode === "count" ? Math.max(0, ...Object.values(values)) : 0;
  const breaks = mode !== "count" ? [] : scale === "linear" ? linearBreaks(max) : breaksFor(max);
  const binOf = (n) => { let b = 0; for (let i = 0; i < breaks.length; i++) if (n >= breaks[i]) b = i; return b; };
  const rampAt = (i) => t.ramp[Math.min(t.ramp.length - 1, Math.round(i * (t.ramp.length - 1) / Math.max(1, breaks.length - 1)))];
  // A country's legend class: its bin (or origin/target), "zero" when measured at none, "none" without data.
  const classOf = (d, id) => (d ? (mode === "count" ? binOf(d.n) : d.kind) : measuredIds?.has(id) ? "zero" : "none");
  const fillFor = (d, id) => {
    if (!d) return measuredIds && !measuredIds.has(id) ? `url(#${hatch})` : t.empty;
    if (mode === "mentions") return d.kind === "origin" ? t.series2 : d.kind === "target" ? t.series1 : t.place;
    return rampAt(binOf(d.n));
  };
  const lit = legendHot ?? legendPin;
  const dimmed = (d, id) => lit !== null && classOf(d, id) !== lit;

  const tipFor = (d, id, name, x, y) => {
    const base = { x, y, flip: x > width * 0.6, label: d?.name || name };
    if (mode === "count") return { ...base, value: d ? `${nf(d.n)} ${unitFor(unit, d.n)}` : !measuredIds ? "None" : measuredIds.has(id) ? `0 ${unit}` : "No data", detail: d && onSelect ? "Click to see them" : null };
    return { ...base, value: d ? L[d.kind] : L.none, detail: d?.sentences?.join("\n\n").slice(0, 300) || null };
  };
  const onMove = (e, d, id, name) => {
    const r = box.el.getBoundingClientRect();
    if (hoverId !== id) setHoverId(id);
    setTip(tipFor(d, id, name, e.clientX - r.left, e.clientY - r.top));
  };

  // A country's middle on the map (where its dot goes, for the small ones): where arcs start and
  // end. Arcs are as thick as the square root of their share of the biggest.
  const arcMax = Math.max(1, ...arcs.map(a => a.n));
  const centreOf = (id) => { const s = shapes.find(x => x.f.id === id); return s ? s.at : SMALL_AT.has(id) ? projection(SMALL_AT.get(id)) : null; };
  // Each arc bowed upward (a quadratic curve), with about how long it is, for the dots' pace.
  const arcLines = arcs.map((a, i) => {
    const p = centreOf(a.from), q = centreOf(a.to);
    if (!p || !q) return null;
    const mx = (p[0] + q[0]) / 2, my = (p[1] + q[1]) / 2, nx = -(q[1] - p[1]) * 0.25, ny = (q[0] - p[0]) * 0.25;
    const [cx, cy] = ny < 0 ? [mx + nx, my + ny] : [mx - nx, my - ny];
    return { a, i, p, q, d: `M${p[0]},${p[1]} Q${cx},${cy} ${q[0]},${q[1]}`, len: Math.hypot(q[0] - p[0], q[1] - p[1]) * 1.1 };
  }).filter(Boolean);
  // Motion: dots along the 40 biggest arcs, and rings at the (up to three) busiest origins.
  const still = useReducedMotion();
  const moving = !still && lit === null;
  const flowing = moving ? [...arcLines].sort((x, y) => y.a.n - x.a.n).slice(0, 40) : [];
  const busiest = (() => {
    if (!moving) return [];
    const m = new Map();
    for (const { a } of arcLines) m.set(a.from, (m.get(a.from) || 0) + a.n);
    const top = [...m].sort((x, y) => y[1] - x[1]);
    return top.slice(0, 3).filter(([, n]) => n >= top[0][1] * 0.25).map(([id]) => id);
  })();

  // The find box: the countries with data, by name. Picking one zooms to it and outlines it.
  const findable = mode === "count"
    ? Object.keys(values).filter(c => numeric[c]).map(c => ({ id: numeric[c], name: names[c] || c }))
    : [...byNumeric.values()].map(d => ({ id: d.numeric, name: d.name }));
  findable.sort((a, b) => a.name.localeCompare(b.name));
  const goTo = (id) => {
    const s = shapes.find(x => x.f.id === id);
    const at = s ? s.at : SMALL_AT.has(id) ? projection(SMALL_AT.get(id)) : null;
    if (!at) return;
    const [[x0, y0], [x1, y1]] = s && s.size >= DOT_BELOW ? path.bounds(s.f) : [[at[0] - 12, at[1] - 12], [at[0] + 12, at[1] + 12]];
    z.zoomTo(x0, y0, x1, y1, 0.6);
    setFocusId(id);
    setTip(null);
  };
  const onFind = (v) => {
    setFind(v);
    const c = findable.find(x => x.name === v);
    if (c) { goTo(c.id); setFind(""); }
  };
  const goRegion = (lonLat) => {
    setFocusId(null);
    if (!lonLat) { z.reset(); return; }
    const [[w, s], [e, n]] = lonLat;
    const xy = [];
    for (let i = 0; i <= 8; i++) { const lon = w + ((e - w) * i) / 8, lat = s + ((n - s) * i) / 8; xy.push(projection([lon, s]), projection([lon, n]), projection([w, lat]), projection([e, lat])); }
    const xs = xy.filter(Boolean).map(p => p[0]), ys = xy.filter(Boolean).map(p => p[1]);
    z.zoomTo(Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys), 1);
  };

  // Countries with data that are too small to point at, and those the 1:110m map leaves out.
  const dotBelow = DOT_BELOW * Math.min(1, width / 900);
  const dots = [
    ...shapes.filter(s => s.size * z.t.k < dotBelow && byNumeric.has(s.f.id)).map(s => ({ key: s.key, id: s.f.id, name: s.f.properties.name, at: s.at })),
    ...(features === COUNTRIES ? SMALL.filter(c => byNumeric.has(c.id)).map(c => ({ key: c.id, id: c.id, name: c.name, at: projection(c.at) })) : []),
  ];

  const tableRows = mode === "count"
    ? Object.entries(values).sort((a, b) => b[1] - a[1]).map(([iso2, n]) => [onSelect ? <button onClick={() => onSelect(iso2, { iso2, n, name: names[iso2] })} style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", font: "inherit", textAlign: "left" }}>{names[iso2] || iso2}</button> : (names[iso2] || iso2), nf(n)])
    : mentions.map(m => [m.name, m.kind === "origin" ? "Origin" : "Target"]);

  // The legend's classes, each a filter: the ramp's steps (or origin and target), then none.
  const legend = mode === "count"
    ? [
      ...breaks.map((b, i) => {
        const hi = (breaks[i + 1] || b + 1) - 1;
        return { key: i, color: rampAt(i), label: scale === "linear" ? `${nf(b)}–${nf(b + breaks[1])}` : i === breaks.length - 1 ? `${nf(b)}+` : hi === b ? nf(b) : `${nf(b)}–${nf(hi)}` };
      }),
      ...(measuredIds ? [{ key: "zero", color: t.empty, label: "0" }, { key: "none", hatch: true, label: "No data" }] : [{ key: "none", color: t.empty, label: "None" }]),
    ]
    : [
      { key: "origin", color: t.series2, label: L.origin }, { key: "target", color: t.series1, label: L.target },
      ...(mentions.some(x => x.kind === "place") ? [{ key: "place", color: t.place, label: L.place }] : []), { key: "none", color: t.empty, label: L.none },
    ];
  const hatchCss = `repeating-linear-gradient(45deg, ${t.empty} 0 2px, ${t.inkDim}66 2px 4px)`;
  // The found country's tooltip, at its middle, until the pointer shows another.
  const focusTip = (() => {
    if (focusId === null || tip) return null;
    const s = shapes.find(x => x.f.id === focusId);
    const at = s ? s.at : SMALL_AT.has(focusId) ? projection(SMALL_AT.get(focusId)) : null;
    if (!at) return null;
    const [x, y] = z.at(at[0], at[1]);
    return tipFor(byNumeric.get(focusId), focusId, s?.f.properties.name || "", x, y);
  })();
  const pill = { background: "none", border: `1px solid ${B.border}`, borderRadius: 999, padding: "3px 10px", fontSize: fs(11), color: t.inkMuted, cursor: "pointer", fontFamily: "inherit" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 8 }}>
        {/* Legend: the classes, each pointable and clickable to light up its countries */}
        <div style={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }} role="group" aria-label={mode === "count" ? "Colour scale: point at a class to light up its countries, click to keep them lit" : "Legend"}>
          {mode === "count" && <span style={{ fontSize: fs(11), color: t.inkDim, marginRight: 6 }}>{unit[0].toUpperCase() + unit.slice(1)}</span>}
          {legend.map(it => (
            <button key={it.key} onClick={() => setLegendPin(p => (p === it.key ? null : it.key))} aria-pressed={legendPin === it.key}
              onPointerEnter={() => setLegendHot(it.key)} onPointerLeave={() => setLegendHot(null)} onFocus={() => setLegendHot(it.key)} onBlur={() => setLegendHot(null)}
              title="Point to light up these countries; click to keep them lit"
              style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 5px", marginRight: 2, borderRadius: 6, border: `1px solid ${legendPin === it.key ? t.ink : "transparent"}`, background: "none", cursor: "pointer", fontFamily: "inherit" }}>
              <span style={{ width: 14, height: 10, borderRadius: 2, background: it.hatch ? hatchCss : it.color, boxShadow: `inset 0 0 0 1px ${t.inkDim}40` }} />
              <span style={{ fontSize: fs(11), color: t.inkMuted, fontVariantNumeric: "tabular-nums" }}>{it.label}</span>
            </button>
          ))}
        </div>
        <span style={{ marginLeft: "auto" }}><TableToggle on={table} onChange={setTable} ui={ui} /></span>
      </div>
      {tools && !table && (
        <div data-noprint style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
          {REGIONS.map(([label, lonLat]) => <button key={label} onClick={() => goRegion(lonLat)} style={pill}>{label}</button>)}
          {findable.length > 0 && <>
            <input type="search" list={`${hatch}find`} value={find} onChange={e => onFind(e.target.value)} placeholder="Find on the map" aria-label="Find a country on the map"
              style={{ marginLeft: "auto", width: 170, background: "transparent", border: `1px solid ${B.border}`, borderRadius: 6, padding: "3px 8px", color: B.text, fontSize: fs(12), fontFamily: "inherit" }} />
            <datalist id={`${hatch}find`}>{findable.map(c => <option key={c.id} value={c.name} />)}</datalist>
          </>}
        </div>
      )}

      {table ? (
        <SimpleTable head={mode === "count" ? ["Country", unit[0].toUpperCase() + unit.slice(1)] : ["Country", "Role"]} rows={tableRows} ui={ui} />
      ) : (
        <div ref={box.ref} style={{ position: "relative", width: "100%", overflow: "hidden", ...z.style }} onPointerLeave={() => { setTip(null); setHoverId(null); }} {...z.handlers}>
          <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={mode === "count" ? `World map of ${unit} by country` : "World map of countries named in the description"} style={{ display: "block", maxWidth: "100%" }}>
            <defs>
              <pattern id={hatch} width={6} height={6} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <rect width={6} height={6} fill={t.empty} />
                <line x1={0} y1={0} x2={0} y2={6} stroke={t.inkDim} strokeOpacity={0.4} strokeWidth={2} />
              </pattern>
            </defs>
            <g transform={z.transform}>
              <path d={globe.sea} fill={t.sea} onPointerMove={() => { setTip(null); setHoverId(null); }} />
              <path d={globe.grid} fill="none" stroke={t.graticule} strokeWidth={1} vectorEffect="non-scaling-stroke" pointerEvents="none" />
              {shapes.map(({ f, key, outline }) => {
                const d = byNumeric.get(f.id);
                return (
                  <path
                    key={key}
                    d={outline}
                    fill={fillFor(d, f.id)}
                    stroke={t.surface}
                    strokeWidth={0.6}
                    vectorEffect="non-scaling-stroke"
                    onPointerMove={(e) => onMove(e, d, f.id, f.properties.name)}
                    onClick={d && onSelect ? () => onSelect(d.iso2, d) : undefined}
                    style={{ cursor: d && onSelect ? "pointer" : "default", transition: "opacity 120ms" }}
                    opacity={dimmed(d, f.id) ? 0.2 : 1}
                  />
                );
              })}
              {/* The country under the pointer, or the one found: outlined on top */}
              {shapes.filter(s => s.f.id && (s.f.id === hoverId || s.f.id === focusId)).map(s => <path key={`on${s.key}`} d={s.outline} fill="none" stroke={t.ink} strokeWidth={1.6} strokeLinejoin="round" vectorEffect="non-scaling-stroke" pointerEvents="none" />)}
              {/* Arcs, bowed upward, orange where they start and blue where they end */}
              {arcLines.map(({ a, i, p, q, d }) => {
                const grad = `${hatch}arc${i}`;
                return (
                  <g key={grad}>
                    <linearGradient id={grad} gradientUnits="userSpaceOnUse" x1={p[0]} y1={p[1]} x2={q[0]} y2={q[1]}><stop offset="0" stopColor={t.series2} /><stop offset="1" stopColor={t.series1} /></linearGradient>
                    <path d={d} fill="none" stroke={`url(#${grad})`} strokeOpacity={lit !== null ? 0.15 : 0.85} strokeWidth={1.2 + 4 * Math.sqrt(a.n / arcMax)} strokeLinecap="round" vectorEffect="non-scaling-stroke" pointerEvents="none" />
                    <path d={d} fill="none" stroke="transparent" strokeWidth={8} vectorEffect="non-scaling-stroke" pointerEvents="stroke" onClick={onArc ? () => onArc(a) : undefined} style={{ cursor: onArc ? "pointer" : "default" }}
                      onPointerMove={(e) => { const r = box.el.getBoundingClientRect(); setHoverId(null); setTip({ x: e.clientX - r.left, y: e.clientY - r.top, flip: e.clientX - r.left > width * 0.6, value: a.label, label: `${nf(a.n)} ${a.n === 1 ? "link" : "links"}`, detail: a.detail || null }); }} />
                  </g>
                );
              })}
              {flowing.map(({ a, i, d, len }) => {
                const share = Math.sqrt(a.n / arcMax);
                return <FlowDots key={`dots${i}`} d={d} len={len} n={1 + Math.round(2 * share)} k={z.t.k} r={2.4 + 1.6 * share} from={t.series2} to={t.series1} />;
              })}
            </g>
            {busiest.map(id => {
              const c = centreOf(id);
              if (!c) return null;
              const [x, y] = z.at(c[0], c[1]);
              // In the ink colour: an origin's own fill is the orange the arcs start in.
              return <Pulse key={`pulse${id}`} x={x} y={y} r={4} color={t.ink} reach={18} />;
            })}
            {spreadDots(dots.map(c => z.at(c.at[0], c.at[1]))).map(([x, y], i, xys) => {
              const c = dots[i];
              const d = byNumeric.get(c.id);
              const on = c.id === hoverId || c.id === focusId;
              // A halo in the card's colour keeps the dot apart from land of the same shade; the
              // hit target reaches halfway to the nearest dot, so neighbours can each be pointed at.
              const near = Math.min(Infinity, ...xys.filter((_, j) => j !== i).map(([ox, oy]) => Math.hypot(ox - x, oy - y)));
              return (
                <g key={c.key} onPointerMove={(e) => onMove(e, d, c.id, c.name)} onClick={onSelect ? () => onSelect(d.iso2, d) : undefined} style={{ cursor: onSelect ? "pointer" : "default" }} opacity={dimmed(d, c.id) ? 0.2 : 1}>
                  <circle cx={x} cy={y} r={on ? 7 : 6} fill={t.surface} pointerEvents="none" />
                  <circle cx={x} cy={y} r={on ? 5.5 : 4.5} fill={fillFor(d)} stroke={t.ink} strokeOpacity={on ? 0.9 : 0.55} strokeWidth={on ? 1.5 : 1} pointerEvents="none" />
                  <circle cx={x} cy={y} r={Math.max(2, Math.min(9, near / 2))} fill="transparent" />
                </g>
              );
            })}
            {/* Exact places: diamonds, the same size at any zoom */}
            {points.map(p => {
              const xy = projection(p.at);
              if (!xy) return null;
              const [x, y] = z.at(xy[0], xy[1]);
              return (
                <g key={p.key} onClick={onPoint ? () => onPoint(p) : undefined} style={{ cursor: onPoint ? "pointer" : "default" }}
                  onPointerMove={() => { setHoverId(null); setTip({ x, y: y - 6, flip: x > width * 0.6, value: p.label, label: p.sub || "", detail: p.detail || null }); }}>
                  <path d={`M${x},${y - 6} L${x + 6},${y} L${x},${y + 6} L${x - 6},${y} Z`} fill={t.ink} stroke={t.halo} strokeWidth={2} paintOrder="stroke" />
                  <circle cx={x} cy={y} r={10} fill="transparent" />
                </g>
              );
            })}
          </svg>
          <Tooltip tip={tip || focusTip} ui={ui} />
          <ZoomControls z={z} ui={ui} />
        </div>
      )}
    </div>
  );
}

// ─── Locator globe ───
// A small globe turned to face one country, the country filled in: for a country's page. It
// faces the country's largest piece of land (France's mainland, not the middle of the Atlantic
// between it and French Guiana). A country too small to see, or one the 1:110m map leaves out,
// gets a ring round its place as well.
export function CountryLocator({ numeric, size = 128, ui }) {
  const { B } = ui;
  const t = vizTokens(B);
  const drawn = useMemo(() => {
    const f = COUNTRIES.find(x => x.id === numeric);
    let at = SMALL_AT.get(numeric) || null;
    if (f) {
      const pieces = f.geometry.type === "MultiPolygon" ? f.geometry.coordinates.map(c => ({ type: "Polygon", coordinates: c })) : [f.geometry];
      at = geoCentroid(pieces.reduce((a, b) => (geoArea(b) > geoArea(a) ? b : a)));
    }
    if (!at) return null;
    const projection = geoOrthographic().rotate([-at[0], -Math.max(-60, Math.min(60, at[1]))]).translate([size / 2, size / 2]).scale(size / 2 - 2).clipAngle(90);
    const path = geoPath(projection);
    const shape = f ? path(f) : null;
    const [[x0, y0], [x1, y1]] = shape ? path.bounds(f) : [[0, 0], [0, 0]];
    return { sphere: path({ type: "Sphere" }), grid: path(geoGraticule().step([30, 30])()), land: path(LAND), shape, ring: !shape || Math.max(x1 - x0, y1 - y0) < 8 ? projection(at) : null };
  }, [numeric, size]);
  if (!drawn) return null;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Where it is on the globe" style={{ flexShrink: 0, display: "block" }}>
      <path d={drawn.sphere} fill={t.sea} stroke={B.border} strokeWidth={1} />
      <path d={drawn.grid} fill="none" stroke={t.graticule} strokeWidth={0.75} />
      <path d={drawn.land} fill={t.inkDim} fillOpacity={0.55} />
      {drawn.shape && <path d={drawn.shape} fill={B.primary} stroke={t.halo} strokeWidth={0.5} />}
      {drawn.ring && <circle cx={drawn.ring[0]} cy={drawn.ring[1]} r={7} fill="none" stroke={B.primary} strokeWidth={2} />}
    </svg>
  );
}

// ─── Horizontal bars (one series) ───
export function BarList({ rows, ui, unit = "", maxRows = 10, extraOther = null, keepOrder = false, onSelect }) {
  const { B, fs } = ui;
  const t = vizTokens(B);
  const [table, setTable] = useState(false);
  const sorted = keepOrder ? [...rows] : [...rows].sort((a, b) => b.value - a.value);
  const head = sorted.slice(0, maxRows);
  const rest = sorted.slice(maxRows).reduce((s, r) => s + r.value, 0) + (extraOther?.value || 0);
  const otherCount = sorted.length - head.length + (extraOther?.count || 0);
  const shown = rest ? [...head, { label: `Everything else${otherCount ? ` (${nf(otherCount)})` : ""}`, value: rest, other: true }] : head;
  const max = Math.max(1, ...shown.filter(r => !r.other).map(r => r.value)); // "Other" never sets the scale
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}><TableToggle on={table} onChange={setTable} ui={ui} /></div>
      {table ? (
        <SimpleTable head={["", unit || "Count"]} rows={sorted.map(r => [r.label, nf(r.value)])} ui={ui} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {shown.map(r => (
            <div key={r.label} title={`${r.label}: ${nf(r.value)}`} style={{ display: "grid", gridTemplateColumns: "minmax(90px, 34%) 1fr", gap: 10, alignItems: "center", padding: "3px 0" }}>
              {!r.href && onSelect && !r.other
                ? <button onClick={() => onSelect(r)} style={{ fontSize: fs(12), color: t.inkMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", font: "inherit" }}>{r.label}</button>
                : r.href
                ? <a href={r.href} style={{ fontSize: fs(12), color: t.inkMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none" }}>{r.label}</a>
                : <span style={{ fontSize: fs(12), color: r.other ? t.inkDim : t.inkMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</span>}
              {onSelect && !r.other ? (
                <button onClick={() => onSelect(r)} title={`See the ${nf(r.value)} behind ${r.label}`} aria-label={`${r.label}: ${nf(r.value)}. Show them`}
                  style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, background: "none", border: "none", padding: "2px 0", cursor: "pointer", font: "inherit" }}>
                  <span style={{ height: 12, width: `${Math.max(1, (r.value / max) * 82)}%`, background: t.series1, borderRadius: "0 4px 4px 0", flexShrink: 0 }} />
                  <span style={{ fontSize: fs(11), color: t.ink, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", textDecoration: "underline", textDecorationColor: t.inkDim, textUnderlineOffset: 2 }}>{nf(r.value)}</span>
                </button>
              ) : (
              <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                {!r.other && <span style={{ height: 12, width: `${Math.max(1, (r.value / max) * 82)}%`, background: t.series1, borderRadius: "0 4px 4px 0", flexShrink: 0 }} />}
                <span style={{ fontSize: fs(11), color: r.other ? t.inkDim : t.ink, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{nf(r.value)}{r.other ? " in total" : ""}</span>
              </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Columns over time (one series), with crosshair-style hover per column band ───
export const niceMax = (v) => {
  if (v <= 5) return 5;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const m = v / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
};

export function ColumnChart({ points, ui, unit = "", formatLabel = (k) => k, height = 190, partialKey = null, onSelect }) {
  const { B, fs } = ui;
  const t = vizTokens(B);
  const boxRef = useRef(null);
  const width = useWidth(boxRef, 720);
  const [hover, setHover] = useState(null);
  const [table, setTable] = useState(false);

  const max = niceMax(Math.max(1, ...points.map(p => p.value)));
  const padL = Math.max(40, nf(max).length * fs(6) + 8), padR = 8, padT = 10, padB = 24;
  const plotW = width - padL - padR, plotH = height - padT - padB;
  const ticks = Number.isInteger(max / 2) ? [0, max / 2, max] : [0, max]; // counts: whole-number ticks only
  const band = plotW / Math.max(1, points.length);
  const barW = Math.max(1, Math.min(24, band - 2));
  const labelEvery = Math.max(1, Math.ceil(points.length / Math.max(2, Math.floor(plotW / 70))));
  const y = (v) => padT + plotH - (v / max) * plotH;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}><TableToggle on={table} onChange={setTable} ui={ui} /></div>
      {table ? (
        <SimpleTable head={["Period", unit || "Count"]} rows={[...points].reverse().map(p => [onSelect && p.value > 0 ? <button onClick={() => onSelect(p)} style={{ background: "none", border: "none", padding: 0, color: "inherit", cursor: "pointer", font: "inherit" }}>{formatLabel(p.key)}</button> : formatLabel(p.key), nf(p.value)])} ui={ui} />
      ) : (
        <div ref={boxRef} style={{ position: "relative", width: "100%" }} onPointerLeave={() => setHover(null)}>
          <svg width={width} height={height} style={{ display: "block", maxWidth: "100%" }} role="img" aria-label={`${unit} over time`}>
            {ticks.map(v => (
              <g key={v}>
                <line x1={padL} x2={width - padR} y1={y(v)} y2={y(v)} stroke={v === 0 ? t.axis : t.grid} strokeWidth={1} />
                <text x={padL - 6} y={y(v) + 4} textAnchor="end" fontSize={fs(10)} fill={t.inkDim} style={{ fontVariantNumeric: "tabular-nums" }}>{nf(v)}</text>
              </g>
            ))}
            {points.map((p, i) => {
              const x = padL + i * band + (band - barW) / 2;
              const top = y(p.value);
              const h = padT + plotH - top;
              const r = Math.min(4, barW / 2, h);
              return (
                <g key={p.key}>
                  {p.value > 0 && (
                    <path d={`M${x},${top + h} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${top + h} Z`}
                      fill={t.series1} opacity={(p.key === partialKey ? 0.4 : 1) * (hover === null || hover === i ? 1 : 0.55)} />
                  )}
                  {i % labelEvery === 0 && <text x={padL + i * band + band / 2} y={height - 6} textAnchor="middle" fontSize={fs(10)} fill={t.inkDim}>{formatLabel(p.key, true)}</text>}
                  {/* Hit target: the whole column band, full height */}
                  <rect x={padL + i * band} y={padT} width={band} height={plotH} fill="transparent" onPointerMove={() => setHover(i)}
                    {...(onSelect && p.value > 0 ? {
                      onClick: () => onSelect(p), tabIndex: 0, role: "button", "aria-label": `${formatLabel(p.key)}: ${nf(p.value)} ${unit}. Show them`,
                      onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(p); } }, onFocus: () => setHover(i),
                      style: { cursor: "pointer", outline: "none" },
                    } : {})} />
                </g>
              );
            })}
            {hover !== null && <line x1={padL + hover * band + band / 2} x2={padL + hover * band + band / 2} y1={padT} y2={padT + plotH} stroke={t.axis} strokeWidth={1} pointerEvents="none" />}
          </svg>
          {hover !== null && points[hover] && (
            <Tooltip ui={ui} tip={{
              x: padL + hover * band + band / 2, y: y(points[hover].value), flip: hover > points.length * 0.6,
              value: `${nf(points[hover].value)} ${unit}${points[hover].key === partialKey ? " so far" : ""}`, label: `${formatLabel(points[hover].key)}${points[hover].key === partialKey ? " (in progress)" : ""}`,
              detail: onSelect && points[hover].value > 0 ? "Click to see them" : null,
            }} />
          )}
        </div>
      )}
    </div>
  );
}

// Fill a sparse { "YYYY-MM": n } or { "YYYY-MM-DD": n } map into a continuous series.
export function continuousSeries(map, unitKind, limit) {
  const keys = Object.keys(map || {}).sort();
  if (!keys.length) return [];
  const out = [];
  if (unitKind === "week") {
    const end = Date.parse(keys[keys.length - 1] + "T00:00:00Z");
    for (let t0 = Date.parse(keys[0] + "T00:00:00Z"); t0 <= end; t0 += 7 * 86400000) {
      const k = new Date(t0).toISOString().slice(0, 10);
      out.push({ key: k, value: map[k] || 0 });
    }
  } else if (unitKind === "year") {
    for (let y = Number(keys[0]); y <= Number(keys[keys.length - 1]); y++) out.push({ key: String(y), value: map[String(y)] || 0 });
  } else if (unitKind === "month") {
    let [y, m] = keys[0].split("-").map(Number);
    const [ly, lm] = keys[keys.length - 1].split("-").map(Number);
    while (y < ly || (y === ly && m <= lm)) {
      const k = `${y}-${String(m).padStart(2, "0")}`;
      out.push({ key: k, value: map[k] || 0 });
      m++; if (m > 12) { m = 1; y++; }
    }
  } else {
    const end = new Date(keys[keys.length - 1] + "T00:00:00Z");
    const start = new Date(Math.max(Date.parse(keys[0] + "T00:00:00Z"), end.getTime() - (limit - 1) * 86400000));
    for (let d = start; d <= end; d = new Date(d.getTime() + 86400000)) {
      const k = d.toISOString().slice(0, 10);
      out.push({ key: k, value: map[k] || 0 });
    }
  }
  return limit ? out.slice(-limit) : out;
}

// ─── Campaign range bars on a shared time axis ───
export function CampaignTimeline({ campaigns, ui }) {
  const { B, fs } = ui;
  const t = vizTokens(B);
  const boxRef = useRef(null);
  const width = useWidth(boxRef, 720);
  const [tip, setTip] = useState(null);
  if (!campaigns.length) return null;
  const labelW = Math.min(220, Math.round(width * 0.34));
  const plotW = width - labelW - 16;
  const t0 = Math.min(...campaigns.map(c => Date.parse(c.first)));
  const t1 = Math.max(...campaigns.map(c => Date.parse(c.last)), t0 + 86400000 * 30);
  const y0 = new Date(t0).getUTCFullYear(), y1 = new Date(t1).getUTCFullYear() + 1;
  const span = Date.UTC(y1, 0, 1) - Date.UTC(y0, 0, 1);
  const x = (ms) => labelW + ((ms - Date.UTC(y0, 0, 1)) / span) * plotW;
  const rowH = 26;
  const height = campaigns.length * rowH + 26;
  const yearStep = Math.max(1, Math.ceil((y1 - y0) / Math.max(2, Math.floor(plotW / 60))));
  const fmt = (d) => new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short" });

  return (
    <div ref={boxRef} style={{ position: "relative", width: "100%" }} onPointerLeave={() => setTip(null)}>
      <svg width={width} height={height} style={{ display: "block", maxWidth: "100%" }} role="img" aria-label="Campaign timeline">
        {Array.from({ length: y1 - y0 + 1 }, (_, i) => y0 + i).filter((yr, i) => i % yearStep === 0).map(yr => (
          <g key={yr}>
            <line x1={x(Date.UTC(yr, 0, 1))} x2={x(Date.UTC(yr, 0, 1))} y1={0} y2={height - 20} stroke={t.grid} strokeWidth={1} />
            <text x={x(Date.UTC(yr, 0, 1))} y={height - 6} textAnchor="middle" fontSize={fs(10)} fill={t.inkDim}>{yr}</text>
          </g>
        ))}
        {campaigns.map((c, i) => {
          const a = x(Date.parse(c.first)), b = Math.max(a + 6, x(Date.parse(c.last)));
          const cy = i * rowH + rowH / 2;
          return (
            <g key={c.id} onPointerMove={(e) => {
              const r = boxRef.current.getBoundingClientRect();
              setTip({ x: e.clientX - r.left, y: e.clientY - r.top, flip: e.clientX - r.left > r.width * 0.6, value: `${fmt(c.first)} – ${fmt(c.last)}`, label: c.name });
            }}>
              <a href={`#/kb/e/${encodeURIComponent(c.attackId || c.id)}`}>
                <text x={labelW - 10} y={cy + 4} textAnchor="end" fontSize={fs(12)} fill={t.inkMuted} style={{ textDecoration: "none" }}>
                  {c.name.length > 30 ? c.name.slice(0, 29) + "…" : c.name}
                </text>
              </a>
              <rect x={a} y={cy - 5} width={b - a} height={10} rx={4} fill={c.self ? t.series2 : t.series1} />
              <rect x={labelW} y={cy - rowH / 2} width={plotW} height={rowH} fill="transparent" />
            </g>
          );
        })}
      </svg>
      <Tooltip tip={tip} ui={ui} />
    </div>
  );
}
